// Rol y permisos granulares de una persona en una app (hoy Muestras). Se administran por la API central (api_vicsa),
// que los guarda en la tabla de usuarios de la app; la app los lee de ahí en cada petición (el cambio es inmediato).
// El ROL (Vendedor, Aprobador, Administrador…) da un conjunto de permisos por defecto; acá se concede o se quita cada uno.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, RotateCcw, ShieldCheck } from 'lucide-react';
import { usuariosAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { diagnosticoAprobaciones } from './diagnosticoAprobaciones';
import ReemplazosApp from './ReemplazosApp';

function Interruptor({ activo, onChange, disabled, etiqueta }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={activo}
      aria-label={etiqueta}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 ${
        activo ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${activo ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

// Canal de la persona: «Todos los canales» o los que se marquen uno por uno (sale de ahí a quién le llegan las aprobaciones de canal)
function SelectorCanales({ alcance, disabled, onGuardar, claseSelect }) {
  const [abierto, setAbierto] = useState(false);
  const [todos, setTodos] = useState(false);
  const [ids, setIds] = useState([]);
  const caja = useRef(null);
  const actuales = alcance.canalIds?.length ? alcance.canalIds : alcance.canalId ? [alcance.canalId] : [];

  useEffect(() => {
    if (!abierto) return undefined;
    setTodos(!!alcance.todosCanales);
    setIds(actuales);
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  const resumen = alcance.todosCanales
    ? 'Todos los canales'
    : actuales.length === 0
      ? '— ninguno —'
      : actuales.length === 1
        ? alcance.canales.find((c) => c.id === actuales[0])?.nombre || `Canal ${actuales[0]}`
        : `${actuales.length} canales`;
  const alternar = (id) => setIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const guardar = async () => {
    const ok = await onGuardar(todos ? { todosCanales: true } : { canalIds: ids });
    // Si no se pudo guardar, el selector queda abierto con lo marcado (y el aviso en rojo dice por qué): no se pierde la selección.
    if (ok !== false) setAbierto(false);
  };

  return (
    <div className="relative" ref={caja}>
      <button type="button" disabled={disabled} onClick={() => setAbierto((v) => !v)} aria-haspopup="listbox" aria-expanded={abierto} className={`${claseSelect} flex items-center justify-between text-left`} title="Define qué solicitudes de canal ve y aprueba">
        <span className="truncate">{resumen}</span>
        <ChevronDown className="w-4 h-4 shrink-0 text-gray-500" />
      </button>
      {abierto && (
        <div className="absolute z-20 mt-1 w-full min-w-[14rem] rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg p-2">
          <label className="flex items-center gap-2 px-2 py-1.5 rounded text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
            <input type="checkbox" checked={todos} onChange={(e) => setTodos(e.target.checked)} />
            Todos los canales
          </label>
          <div className={`max-h-48 overflow-auto border-t border-gray-100 dark:border-gray-700 mt-1 pt-1 ${todos ? 'opacity-50' : ''}`}>
            {alcance.canales.map((c) => (
              <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer">
                <input type="checkbox" disabled={todos} checked={todos || ids.includes(c.id)} onChange={() => alternar(c.id)} />
                {c.nombre}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={() => setAbierto(false)} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200">Cancelar</button>
            <button type="button" onClick={guardar} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700">Guardar</button>
          </div>
        </div>
      )}
    </div>
  );
}

const ETIQUETA = {
  concedido: { texto: 'Concedido', clase: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  quitado: { texto: 'Quitado', clase: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
};

export default function PermisosFinosApp({ userId, appCodigo, appNombre, tieneAcceso }) {
  const { showToast } = useToast();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(null); // permiso que se está guardando, o 'todos'
  const [cubiertos, setCubiertos] = useState([]); // personas a las que reemplaza hoy (con lo que heredaría de cada una)
  const [verPermisos, setVerPermisos] = useState(false); // la lista completa de permisos va plegada: lo importante es el resumen
  const [gruposAbiertos, setGruposAbiertos] = useState(() => new Set(['Aprobaciones']));

  const cargar = useCallback(async () => {
    try {
      const { data } = await usuariosAPI.getPermisosFinosApp(userId, appCodigo);
      setDatos(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudieron cargar los permisos.');
    }
  }, [userId, appCodigo]);

  useEffect(() => {
    setDatos(null);
    cargar();
  }, [cargar]);

  const cambiar = async (p) => {
    setGuardando(p.permiso);
    try {
      await usuariosAPI.setPermisoFinoApp(userId, appCodigo, { permiso: p.permiso, activo: !p.efectivo });
      await cargar();
    } catch (err) {
      showToast('Error', err.response?.data?.message || 'No se pudo guardar el permiso', 'error');
    } finally {
      setGuardando(null);
    }
  };

  const cambiarRol = async (rol) => {
    if (!rol) return;
    setGuardando('rol');
    try {
      await usuariosAPI.setRolApp(userId, appCodigo, { rol });
      await cargar();
      showToast('Listo', 'Rol actualizado', 'success');
    } catch (err) {
      showToast('Error', err.response?.data?.message || 'No se pudo cambiar el rol', 'error');
    } finally {
      setGuardando(null);
    }
  };

  // Canal (paso 1) y Product Manager (paso 2): de ellos sale a quién le llegan las aprobaciones en la app
  const cambiarAlcance = async (clave, valor) => {
    setGuardando(clave);
    try {
      await usuariosAPI.setDatosApp(userId, appCodigo, { [clave]: valor ? Number(valor) : 0 });
      await cargar();
      showToast('Listo', 'Product Manager actualizado', 'success');
    } catch (err) {
      showToast('Error', err.response?.data?.message || 'No se pudo guardar el cambio', 'error');
    } finally {
      setGuardando(null);
    }
  };

  const guardarCanales = async (payload) => {
    setGuardando('canalId');
    try {
      await usuariosAPI.setDatosApp(userId, appCodigo, payload);
      await cargar();
      showToast('Listo', 'Canal actualizado', 'success');
      return true;
    } catch (err) {
      showToast('Error', err.response?.data?.message || 'No se pudo guardar el canal', 'error');
      return false;
    } finally {
      setGuardando(null);
    }
  };

  const restablecer = async () => {
    setGuardando('todos');
    try {
      await usuariosAPI.restablecerPermisosFinosApp(userId, appCodigo);
      await cargar();
      showToast('Listo', 'Se volvió a los permisos que da el perfil', 'success');
    } catch (err) {
      showToast('Error', err.response?.data?.message || 'No se pudo restablecer', 'error');
    } finally {
      setGuardando(null);
    }
  };

  if (error) return <div className="px-5 pb-5 text-sm text-red-600">{error}</div>;
  if (!datos) return <div className="px-5 pb-5 text-sm text-gray-500">Cargando permisos…</div>;

  const { rol, roles, acceso, registrado, totales, grupos, alcance } = datos;
  const claseSelect = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50';
  const claseEtiqueta = 'block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1';
  const alternarGrupo = (nombre) => setGruposAbiertos((prev) => {
    const sig = new Set(prev);
    if (sig.has(nombre)) sig.delete(nombre); else sig.add(nombre);
    return sig;
  });
  const ajustes = grupos.flatMap((g) => g.permisos.filter((p) => p.ajuste));

  // Resumen de lo que podrá hacer: lo que sí y los avisos arriba; los pasos que no puede decidir, en una sola línea
  const lineas = registrado && rol ? diagnosticoAprobaciones(appCodigo, new Set(grupos.flatMap((g) => g.permisos.filter((p) => p.efectivo).map((p) => p.permiso))), alcance || {}, cubiertos) : [];
  const esPasoNo = (l) => l.tipo === 'no' && l.texto.endsWith(': no puede decidirlo.');
  const sinPasos = lineas.filter(esPasoNo).map((l) => l.texto.replace(': no puede decidirlo.', ''));
  const lineasVisibles = lineas.filter((l) => !esPasoNo(l));
  const hayAviso = lineas.some((l) => l.tipo === 'aviso');
  const estilo = {
    ok: { icono: '✓', clase: 'text-green-700 dark:text-green-400' },
    aviso: { icono: '!', clase: 'text-amber-700 dark:text-amber-400' },
    no: { icono: '—', clase: 'text-gray-500 dark:text-gray-400' },
  };

  return (
    <div className="space-y-4">
      {/* 1. Quién es en la app: rol, canal y Product Manager en una sola fila */}
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
          <label className="block">
            <span className={claseEtiqueta}>Rol en {appNombre}</span>
            <select value={rol?.rol || ''} disabled={guardando !== null} onChange={(e) => cambiarRol(e.target.value)} className={claseSelect}>
              {!rol && <option value="">Elegir rol…</option>}
              {roles.map((r) => (
                <option key={r.rol} value={r.rol}>{r.nombre}</option>
              ))}
            </select>
          </label>
          {alcance && alcance.canales?.length > 0 && (
            <>
              <label className="block">
                <span className={claseEtiqueta}>Canal</span>
                <SelectorCanales alcance={alcance} disabled={guardando !== null || !registrado} onGuardar={guardarCanales} claseSelect={claseSelect} />
              </label>
              <label className="block">
                <span className={claseEtiqueta}>Product Manager</span>
                <select value={alcance.pmId || 0} disabled={guardando !== null || !registrado} onChange={(e) => cambiarAlcance('pmId', e.target.value)} className={claseSelect} title="Define qué solicitudes de su categoría ve y aprueba">
                  <option value={0}>— ninguno —</option>
                  {alcance.pms.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              </label>
            </>
          )}
        </div>
        {totales.ajustados > 0 && (
          <button
            type="button"
            disabled={guardando !== null}
            onClick={restablecer}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 shrink-0"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Volver a lo del perfil
          </button>
        )}
      </div>

      {!registrado ? (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          {alcance?.canales?.length > 0
            ? `Todavía no está registrada en ${appNombre}. Al elegir un rol se registra; el canal y el Product Manager se eligen aquí y el centro de costo se completa en Gestión Usuarios de ${appNombre}.`
            : `Todavía no está registrada en ${appNombre}. Al elegir un rol se registra y puede entrar.`}
        </p>
      ) : !rol ? (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Sin rol la persona no tiene permisos en {appNombre}: elige uno para que pueda usar la app.
        </p>
      ) : null}
      {(!tieneAcceso || acceso === false) && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          La persona no tiene acceso a la app: otorga primero el acceso para que estos permisos tengan efecto.
        </p>
      )}

      {/* 2. Qué podrá hacer, en pocas líneas */}
      {lineas.length > 0 && (
        <div className={`rounded-lg border px-4 py-3 ${hayAviso ? 'border-amber-300 bg-amber-50/60 dark:border-amber-700 dark:bg-amber-950/20' : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40'}`}>
          <h5 className="text-[11px] font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1.5">Qué verá y podrá aprobar</h5>
          <ul className="space-y-1">
            {lineasVisibles.map((l, i) => (
              <li key={i} className={`flex gap-2 text-[13px] leading-snug ${estilo[l.tipo].clase}`}>
                <span className="font-bold w-4 shrink-0 text-center">{estilo[l.tipo].icono}</span>
                <span>{l.texto}</span>
              </li>
            ))}
            {sinPasos.length > 0 && (
              <li className={`flex gap-2 text-[13px] leading-snug ${estilo.no.clase}`}>
                <span className="font-bold w-4 shrink-0 text-center">{estilo.no.icono}</span>
                <span>No puede decidir: {sinPasos.join(', ')}.</span>
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Reemplazos de aprobadores (Tecnoboga y Vicsa) */}
      {(appCodigo === 'muestras' || appCodigo === 'muestras-vicsa') && registrado && rol && <ReemplazosApp userId={userId} appCodigo={appCodigo} onCargado={setCubiertos} />}

      {/* 3. Solo lo que se ajustó a mano respecto del perfil */}
      {ajustes.length > 0 && (
        <div>
          <h5 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">Ajustes sobre el perfil ({ajustes.length})</h5>
          <div className="flex flex-wrap gap-1.5">
            {ajustes.map((p) => (
              <span key={p.permiso} title={`${p.permiso}: ${p.ajuste === 'concedido' ? 'concedido a mano' : 'quitado a mano'}`} className={`inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-full text-xs font-medium ${ETIQUETA[p.ajuste].clase}`}>
                <span className="font-bold">{p.ajuste === 'concedido' ? '+' : '−'}</span>
                <span className="truncate">{p.descripcion}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 4. Todos los permisos, plegados */}
      <div>
        <button
          type="button"
          onClick={() => setVerPermisos((v) => !v)}
          aria-expanded={verPermisos}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/60 text-left"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
            <ShieldCheck className="w-4 h-4 text-blue-500" />
            {verPermisos ? 'Ocultar' : 'Ver y editar'} los permisos uno por uno
            <span className="font-normal text-gray-500 dark:text-gray-400">({totales.activos} de {totales.total} activos)</span>
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${verPermisos ? 'rotate-180' : ''}`} />
        </button>

        {verPermisos && (
          <div className="mt-2 space-y-2">
            {grupos.map((g) => {
              const abierto = gruposAbiertos.has(g.grupo);
              const activos = g.permisos.filter((p) => p.efectivo).length;
              return (
                <section key={g.grupo} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => alternarGrupo(g.grupo)}
                    aria-expanded={abierto}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900/40 hover:bg-gray-100 dark:hover:bg-gray-900/70 text-left"
                  >
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">{g.grupo}</span>
                    <span className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      {activos} de {g.permisos.length} activos
                      <ChevronDown className={`w-4 h-4 transition-transform ${abierto ? 'rotate-180' : ''}`} />
                    </span>
                  </button>
                  {abierto && (
                    <ul className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 divide-gray-100 dark:divide-gray-700/60 bg-white dark:bg-gray-800">
                      {g.permisos.map((p) => (
                        <li key={p.permiso} title={p.permiso} className="flex items-center gap-2.5 px-3 py-1.5">
                          <Interruptor activo={p.efectivo} disabled={guardando !== null || !registrado} onChange={() => cambiar(p)} etiqueta={p.descripcion} />
                          <span className="min-w-0 flex-1 text-[13px] leading-snug text-gray-800 dark:text-gray-100">{p.descripcion}</span>
                          {p.ajuste && (
                            <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${ETIQUETA[p.ajuste].clase}`}>{ETIQUETA[p.ajuste].texto}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
            <p className="text-[11px] text-gray-500 dark:text-gray-400">Pasa el cursor sobre un permiso para ver su nombre técnico. Los cambios se aplican en la app en hasta un minuto; no hace falta cerrar sesión.</p>
          </div>
        )}
      </div>
    </div>
  );
}
